<template>
  <div class="dw on">
    <div class="dwh">
      <div>
        <div class="dwt">{{ title }}</div>
        <span class="dws">
          OF-{{ offer.id }} ·
          Global Offer <a v-if="globalOfferHref" class="nlink" :href="globalOfferHref" target="_blank" rel="noopener">{{ offer.global_offer_id }}</a><template v-else>{{ offer.global_offer_id || '—' }}</template>
          ·
          Affise <a v-if="affiseOfferHref" class="nlink" :href="affiseOfferHref" target="_blank" rel="noopener">{{ offer.affise_id }}</a><template v-else>{{ offer.affise_id }}</template>
        </span>
      </div>
      <div class="dwn">
        <button type="button" title="Previous (↑)" @click="$emit('prev')">↑</button>
        <button type="button" title="Next (↓)" @click="$emit('next')">↓</button>
      </div>
      <button type="button" class="dwx" @click="$emit('close')">✕</button>
    </div>
    <div class="dwb">
      <div class="cols">
        <div>
          <div class="grp">
            <div class="grph">Identity</div>
            <div class="grpb">
              <div class="fd">
                <label>Affise ID</label>
                <input type="number" :value="offer.affise_id" @input="patch('affise_id', Number($event.target.value))">
              </div>
              <div class="fd">
                <label>Global Offer ID</label>
                <input :value="offer.global_offer_id || ''" readonly class="ro-in">
              </div>
              <div class="fd wide">
                <label>Offer name</label>
                <input :value="offer.name" style="font-family:var(--ui);font-size:12px" @input="patch('name', $event.target.value)">
              </div>
              <div class="fd wide">
                <label>Offer region <span class="lock">not functional</span></label>
                <GeoRegionSelect kind="region" :geo="adSet.geo" :value="offer.region" :disabled="true" @input="patch('region', $event)" />
              </div>
            </div>
          </div>

          <div class="grp">
            <div class="grph">Weight &amp; auto adjustment</div>
            <div class="grpb">
              <div class="fd">
                <label>Weight</label>
                <input type="number" :value="offer.weight" @input="onWeight($event.target.value)">
                <span class="hint">{{ weightPct }}% of pool</span>
              </div>
              <div class="fd">
                <label>Auto weight value</label>
                <input :value="autoWeightLabel" readonly class="ro-in awv">
                <span class="hint">±15% band · ±6 steps · 0.20 reversion</span>
              </div>
              <label class="ck">
                <input type="checkbox" class="cb" :checked="offer.auto_weight_enabled" :disabled="reserved" @change="patch('auto_weight_enabled', $event.target.checked)">
                Auto Weight
        <span v-if="reserved" class="lock">reserved — weight below 17 never adjusts</span>
              </label>
              <div class="ro">Typing a new weight <b>re-anchors the base</b>; the drift band recentres on it.</div>
            </div>
          </div>

          <div class="grp">
            <div class="grph">Caps &amp; frequency</div>
            <div class="grpb">
              <div class="fd wide">
                <label>Clicks cap</label>
                <input type="number" :value="offer.cap" @input="patch('cap', Number($event.target.value))">
                <span class="hint">0 = infinite</span>
              </div>
              <div class="fd wide">
                <label>Frequency</label>
                <FreqSeg small :value="offer.frequency" @input="patch('frequency', $event)" />
              </div>
              <div class="fd wide">
                <label>IP frequency</label>
                <FreqSeg small :value="offer.ip_frequency" @input="patch('ip_frequency', $event)" />
              </div>
              <div class="fd">
                <label>IP frequency level</label>
                <select :value="offer.ip_frequency_level" @change="patch('ip_frequency_level', $event.target.value)">
                  <option v-for="l in ipLevels" :key="l.value" :value="l.value">{{ l.text }}</option>
                </select>
              </div>
              <div class="fd">
                <label>Delivery frequency %</label>
                <input type="number" :value="offer.delivery_frequency" @input="patch('delivery_frequency', Number($event.target.value))">
                <span class="hint">random throttle, last gate</span>
              </div>
              <div class="fd wide" v-if="offer.click_offer_id">
                <label>CTR % — synthetic click rate</label>
                <input type="number" step="0.01" :value="offer.click_ctr" @input="patch('click_ctr', Number($event.target.value))">
                <span class="hint">clicks per 100 VTA impressions</span>
              </div>
            </div>
          </div>

          <div class="grp">
            <div class="grph">Pause</div>
            <div class="grpb">
              <template v-if="!offer.paused">
                <div class="fd">
                  <label>Duration</label>
                  <input type="number" min="1" v-model.number="pauseVal">
                </div>
                <div class="fd">
                  <label>Unit</label>
                  <select v-model="pauseUnit">
                    <option v-for="u in pauseUnits" :key="'pu'+u">{{ u }}</option>
                  </select>
                </div>
                <div class="ck">
                  <button type="button" class="btn btn-s" @click="pauseOffer">Pause offer</button>
                  <span class="hint">auto-resumes after</span>
                </div>
              </template>
              <div v-else class="ck">
                <button type="button" class="btn btn-s" @click="resetPause">Reset pause</button>
                <span class="hint">
                  paused{{ offer.pause_by_user ? ' by ' + offer.pause_by_user : '' }}
                  <template v-if="pauseLeft"> — Release in {{ pauseLeft }}</template>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div class="grp">
            <div class="grph">Targeting filters</div>
            <div class="grpb">
              <div class="fd">
                <label>OS version</label>
                <div style="display:flex;gap:4px">
                  <input :value="adSetOs" readonly class="ro-in" style="width:52px">
                  <select style="width:56px" :value="osOp" @change="osOp = $event.target.value; commitOs()">
                    <option>>=</option>
                    <option>&lt;=</option>
                    <option>=</option>
                  </select>
                  <input :value="osVer" placeholder="14.0" @input="osVer = $event.target.value; commitOs()">
                </div>
              </div>
              <div class="fd">
                <label>Connection type</label>
                <div class="fseg">
                  <button type="button" :class="{ on: connFlags(offer.connection_type).wifi }" @click="toggleConn('wifi')">WiFi</button>
                  <button type="button" :class="{ on: connFlags(offer.connection_type).cell }" @click="toggleConn('cell')">Cellular</button>
                </div>
              </div>
              <div class="fd">
                <label>Traffic type</label>
                <div class="fseg">
                  <button type="button" :class="{ on: !!offer.traffic_in_app }" @click="toggleBit('traffic_in_app', 'traffic_mobile')">In-App</button>
                  <button type="button" :class="{ on: !!offer.traffic_mobile }" @click="toggleBit('traffic_mobile', 'traffic_in_app')">Mobile Web</button>
                </div>
              </div>
              <div class="fd">
                <label>Creative type</label>
                <div class="fseg">
                  <button type="button" :class="{ on: !!offer.creative_tag }" @click="toggleBit('creative_tag', 'creative_img')">TAG</button>
                  <button type="button" :class="{ on: !!offer.creative_img }" @click="toggleBit('creative_img', 'creative_tag')">IMG</button>
                </div>
              </div>
              <label class="ck">
                <input type="checkbox" class="cb" :checked="!!offer.is_device_id_traffic" @change="patch('is_device_id_traffic', $event.target.checked)">
                Device ID Traffic Only
              </label>
              <div class="ro">No device ID → offer drops out of the <b>weight pool</b>; traffic redistributes by weight.</div>
            </div>
          </div>

          <div class="grp">
            <div class="grph">PID rotator <span class="lock v">inherited</span></div>
            <div class="grpb">
              <label class="ck"><input type="checkbox" class="cb" :checked="pidRotatorOn" disabled> PID Rotator</label>
              <label class="ck"><input type="checkbox" class="cb" :checked="!!offer.is_global_offer_new_pid_rotating" disabled> New PID Rotator</label>
              <div class="ro">PID <b>{{ offer.pid || '—' }}</b> · last rotate <b>{{ offer.last_rotate || '—' }}</b> · every <b>{{ offer.pid_rotating_period || '—' }}h</b></div>
            </div>
          </div>

          <div class="grp">
            <div class="grph">URL overrides</div>
            <div class="grpb one">
              <div class="fd">
                <label>Custom subs query</label>
                <input :value="offer.subs_query" placeholder="overrides tracking URL query" @input="patch('subs_query', $event.target.value)">
              </div>
              <div class="fd">
                <label>Custom URL</label>
                <input :value="offer.custom_url" placeholder="full override URL" @input="patch('custom_url', $event.target.value)">
                <span class="hint">placeholders: {date_q} · {hour_letter}</span>
              </div>
              <div class="urlbox">{{ trackingUrl }}</div>
            </div>
          </div>

          <div class="grp">
            <div class="grph">IDFA blacklisting</div>
            <div class="grpb one">
              <div class="fd">
                <label>Attach file</label>
                <select :value="''" @change="attachFile($event.target.value)">
                  <option value="">Select file…</option>
                  <option v-for="f in availableFiles" :key="f.id" :value="f.id">{{ f.id }} · {{ f.title }}</option>
                </select>
              </div>
              <div class="mini-tbl">
                <table v-if="attachedFiles.length">
                  <thead><tr><th>File ID</th><th>Title</th><th style="width:26px"></th></tr></thead>
                  <tbody>
                    <tr v-for="f in attachedFiles" :key="f.id">
                      <td style="font-family:var(--mono)">{{ f.id }}</td>
                      <td>{{ f.title }}</td>
                      <td><button type="button" class="ib del" @click="detachFile(f)">🗑</button></td>
                    </tr>
                  </tbody>
                </table>
                <div v-else class="mini-empty">This table is empty</div>
              </div>
            </div>
          </div>

          <div class="grp">
            <div class="grph">Subs blacklisting <span class="lock" :class="{ r: subStats.n }">{{ subStats.n }} rule{{ subStats.n === 1 ? '' : 's' }} · {{ subStats.vals }} values</span></div>
            <div class="grpb one">
              <SubRulesEditor :rules="offer.sub_rules" @change="$emit('sub-rules')" />
            </div>
          </div>

          <div class="grp">
            <div class="grph">Notes</div>
            <div class="grpb one">
              <div class="fd">
                <label>Comments</label>
                <input :value="offer.comments" style="font-family:var(--ui)" @input="patch('comments', $event.target.value)">
              </div>
              <div class="fd">
                <label>Advertiser requests</label>
                <input :value="offer.advertiser_requests" style="font-family:var(--ui)" @input="patch('advertiser_requests', $event.target.value)">
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="dwf">
      <button type="button" class="btn" @click="loadDefault">↺ Load default</button>
      <span class="grow"></span>
      <label class="ckl" v-if="selectedCount > 1">
        <input type="checkbox" class="cb" v-model="applyAll"> Apply to all selected
      </label>
      <button type="button" class="btn" @click="$emit('close')">Cancel</button>
      <button type="button" class="btn primary" @click="saveClose">Save offer changes</button>
    </div>
  </div>
</template>

<script>
import FreqSeg from './FreqSeg.vue'
import SubRulesEditor from './SubRulesEditor.vue'
import GeoRegionSelect from './GeoRegionSelect.vue'
import { IP_FRQ_LEVELS, PAUSE_UNITS, ruleValCount, connFlags, connFromFlags, pauseToMinutes, minutesToPause, pauseReleaseLeft } from '../../v2/status'

export default {
  name: 'OfferDrawer',
  components: { FreqSeg, SubRulesEditor, GeoRegionSelect },
  props: {
    offer: { type: Object, required: true },
    adSet: { type: Object, default: () => ({}) },
    selectedIds: { type: Array, default: () => [] },
    campId: { type: [Number, String], required: true }
  },
  data() {
    return {
      ipLevels: IP_FRQ_LEVELS,
      pauseUnits: PAUSE_UNITS,
      pauseVal: 1,
      pauseUnit: 'Hours',
      applyAll: false,
      osOp: '>=',
      osVer: '',
      catalog: [],
      nowMs: Date.now(),
      pauseClock: null
    }
  },
  computed: {
    title() {
      const n = this.offer.name || ''
      return n.length > 62 ? n.slice(0, 62) + '…' : n
    },
    reserved() {
      return this.offer.weight < 17
    },
    autoWeightLabel() {
      if (this.offer.auto_weight_current == null) return '—'
      return String(this.offer.auto_weight_current)
    },
    weightPct() {
      const pool = (this.adSet.offers || []).filter(o => !o.paused)
      const total = pool.reduce((s, o) => s + (Number(o.weight) || 0), 0)
      if (!total || this.offer.paused) return '0.00'
      return ((Number(this.offer.weight) || 0) / total * 100).toFixed(2)
    },
    adSetOs() {
      return this.adSet.os || ''
    },
    pidRotatorOn() {
      return !!(this.offer.is_global_offer_pid_rotating || this.offer.is_rotating)
    },
    affiseOfferHref() {
      const id = this.offer && this.offer.affise_id
      return id ? 'https://offers-apptimus.affise.com/cpa/offer/' + id : ''
    },
    globalOfferHref() {
      const id = Number(this.offer && this.offer.global_offer_id)
      if (!id) return ''
      if (this.offer.is_new_global_offer || id >= 40000) {
        return '/apps/smartlinks/new-global-offers/create?id=' + id
      }
      return '/apps/smartlinks/global-offers-edit?id=' + id + '&isVta=' + (this.offer.vta ? 1 : 0)
    },
    trackingUrl() {
      if (this.offer.custom_url) return this.offer.custom_url
      const affise = this.offer.affise_id || ''
      let subs = this.offer.subs_query || 'pid={pid}&sub1={sub1}&sub2={sub2}&sub4={sub4}&sub5={sub5}&sub7={sub7}&sub8={sub8}&sub6={sub6}&sub3={sub3}'
      if (this.offer.pid) subs = subs.replace('{pid}', this.offer.pid)
      return `https://go.g2app.net/click?offer_id=${affise}&${subs}`
    },
    attachedFiles() {
      const ids = (this.offer.bl_files || []).map(f => f.id)
      return ids.map(id => {
        const hit = this.catalog.find(f => f.id === id)
        return { id, title: (hit && hit.title) || (this.offer.bl_files.find(f => f.id === id) || {}).title || '' }
      })
    },
    availableFiles() {
      const used = new Set((this.offer.bl_files || []).map(f => f.id))
      return this.catalog.filter(f => !used.has(f.id))
    },
    subStats() {
      const rules = this.offer.sub_rules || []
      return { n: rules.length, vals: rules.reduce((n, r) => n + ruleValCount(r), 0) }
    },
    selectedCount() {
      return this.selectedIds.length
    },
    pauseLeft() {
      return pauseReleaseLeft(this.offer, this.nowMs)
    }
  },
  watch: {
    offer: {
      immediate: true,
      handler() {
        this.parseOs()
        this.initPause()
      }
    }
  },
  async created() {
    await this.loadCatalog()
    this.pauseClock = setInterval(() => { this.nowMs = Date.now() }, 1000)
  },
  beforeDestroy() {
    clearInterval(this.pauseClock)
  },
  methods: {
    connFlags,
    patch(field, value) {
      this.$emit('patch', field, value)
    },
    toggleConn(which) {
      const f = connFlags(this.offer.connection_type)
      const next = { wifi: f.wifi, cell: f.cell }
      next[which] = !next[which]
      if (!next.wifi && !next.cell) return
      this.patch('connection_type', connFromFlags(next.wifi, next.cell))
    },
    toggleBit(field, other) {
      const on = !!this.offer[field]
      if (on && !this.offer[other]) return
      this.patch(field, on ? 0 : 1)
    },
    onWeight(raw) {
      const n = Number(raw)
      this.patch('weight', n)
      if (n < 17 && this.offer.auto_weight_enabled) this.patch('auto_weight_enabled', false)
    },
    parseOs() {
      const s = String(this.offer.os_version || '').trim()
      const m = s.match(/^(>=|<=|=)(.*)$/)
      if (m) {
        this.osOp = m[1]
        this.osVer = m[2].trim()
      } else {
        this.osOp = '>='
        this.osVer = s
      }
    },
    commitOs() {
      const ver = (this.osVer || '').trim()
      this.patch('os_version', ver ? this.osOp + ver : '')
    },
    async loadCatalog() {
      if (this.$store.state.blFiles && this.$store.state.blFiles.length) {
        this.catalog = this.$store.state.blFiles
        return
      }
      try {
        const r = await this.$http.get(this.resources.blacklisting.getFiles)
        this.catalog = await r.json()
        this.$store.state.blFiles = this.catalog
      } catch (e) {
        this.catalog = []
      }
    },
    async attachFile(id) {
      if (!id) return
      const file = this.catalog.find(f => String(f.id) === String(id))
      if (!file) return
      if (!window.confirm('Attach "' + file.title + '" to this offer?')) return
      try {
        await this.$http.post(this.resources.blacklisting.offerBlFiles(this.offer.id), {
          blfile_id: Number(id),
          offer_placement_id: this.offer.placement_id,
          offer_campaign_id: Number(this.campId)
        })
        const files = (this.offer.bl_files || []).concat([{ id: Number(id), title: file.title }])
        this.$set(this.offer, 'bl_files', files)
        this.notifySuccess('IDFA file attached')
      } catch (e) {
        this.notifyError('Could not attach file')
      }
    },
    async detachFile(f) {
      if (!window.confirm('Remove this IDFA file?')) return
      try {
        await this.$http.delete(this.resources.blacklisting.offerBlFiles(this.offer.id) + f.id)
        this.$set(this.offer, 'bl_files', (this.offer.bl_files || []).filter(x => x.id !== f.id))
      } catch (e) {
        this.notifyError('Could not remove file')
      }
    },
    initPause() {
      const parts = minutesToPause(this.offer && this.offer.pause_duration_minutes)
      this.pauseVal = parts.pauseVal
      this.pauseUnit = parts.pauseUnit
    },
    pauseOffer() {
      const minutes = pauseToMinutes(this.pauseVal, this.pauseUnit)
      this.$set(this.offer, 'pause_ts', new Date().toISOString())
      this.patch('pause_duration_minutes', minutes)
      this.patch('paused', true)
      this.notifySuccess('Offer paused for ' + this.pauseVal + ' ' + this.pauseUnit)
    },
    resetPause() {
      this.patch('pause_duration_minutes', 0)
      this.patch('paused', false)
      this.notifySuccess('Offer pause reset')
    },
    loadDefault() {
      this.patch('cap', 150000)
      this.patch('frequency', 24)
      this.patch('ip_frequency', 12)
      this.patch('ip_frequency_level', 'l3')
      this.patch('delivery_frequency', 100)
      this.patch('connection_type', 'all')
      this.patch('traffic_in_app', 1)
      this.patch('traffic_mobile', 1)
      this.patch('creative_tag', 1)
      this.patch('creative_img', 1)
      this.osOp = '>='
      this.osVer = ''
      this.commitOs()
      this.patch('is_device_id_traffic', false)
      this.patch('subs_query', '')
      this.patch('custom_url', '')
      this.notifySuccess('Loaded default setup')
    },
    saveClose() {
      if (this.applyAll && this.selectedCount > 1) this.$emit('apply-selected')
      this.$emit('close')
    }
  }
}
</script>
